import {
  Anchor,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { normalizeLanguage } from "../i18n";
import { RestApi } from "../shared/RestApi";
import { NavBarContainer } from "./NavBarContainer";

const FAKE_EMAILS = [
  {
    action: "distributorSignUp",
    labelKey: "distributorSignUp",
  },
  {
    action: "activationLink",
    labelKey: "activationLink",
  },
  {
    action: "sendEmailLicense",
    labelKey: "sendEmailLicense",
  },
  {
    action: "sendCongratulationEmail",
    labelKey: "congratulationEmail",
  },
  {
    action: "sendBoughtLicensesMail",
    labelKey: "boughtLicensesMail",
  },
  {
    action: "sendDemoDownloadThankYou",
    labelKey: "demoDownloadThankYou",
  },
] as const;

export function FakeEmailPage() {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);
  const [testAddress, setTestAddress] = React.useState("");

  const { data, isPending, isError } = useQuery({
    queryKey: ["fakeEmails", language],
    queryFn: async () =>
      Promise.all(
        FAKE_EMAILS.map(async (email) => ({
          ...email,
          message: await RestApi.getFakeEmail(email.action, language),
        })),
      ),
  });

  const sendMutation = useMutation({
    mutationFn: (action: string) =>
      RestApi.sendFakeEmailTest(action, testAddress.trim(), language),
    onSuccess: (_data, action) => {
      notifications.show({
        color: "green",
        message: t("fakeEmail.sendSuccess", {
          email: testAddress.trim(),
          action,
        }),
      });
    },
    onError: () => {
      notifications.show({
        color: "red",
        message: t("fakeEmail.sendError"),
      });
    },
  });

  const canSend = testAddress.trim().length > 0 && !sendMutation.isPending;

  return (
    <NavBarContainer title={t("fakeEmail.title")}>
      <Stack gap="xl">
        <Text size="sm" c="dimmed">
          {t("fakeEmail.description")}
        </Text>
        <TextInput
          label={t("fakeEmail.testAddress")}
          placeholder={t("fakeEmail.testAddressPlaceholder")}
          value={testAddress}
          onChange={(event) => setTestAddress(event.currentTarget.value)}
          type="email"
          maw={420}
        />
        {isPending && <Text>{t("fakeEmail.loading")}</Text>}
        {isError && <Text c="red">{t("fakeEmail.loadError")}</Text>}
        {data?.map((email) => (
          <Stack key={email.action} gap="xs">
            <Group justify="space-between" align="center" wrap="wrap">
              <Title order={4}>{t(`fakeEmail.${email.labelKey}`)}</Title>
              <Button
                size="xs"
                color="brand"
                disabled={!canSend}
                loading={
                  sendMutation.isPending && sendMutation.variables === email.action
                }
                onClick={() => sendMutation.mutate(email.action)}
              >
                {t("fakeEmail.sendTest")}
              </Button>
            </Group>
            <Text>
              <Text span fw={600}>
                {t("fakeEmail.subject")}:{" "}
              </Text>
              {email.message.subject}
            </Text>
            <Text fw={600}>{t("fakeEmail.body")}:</Text>
            <iframe
              title={t(`fakeEmail.${email.labelKey}`)}
              srcDoc={email.message.body}
              style={{
                width: "100%",
                minHeight: 320,
                border: "1px solid var(--mantine-color-gray-4)",
                borderRadius: 4,
                background: "white",
              }}
            />
          </Stack>
        ))}
        <Anchor component={Link} to="/admin">
          {t("fakeEmail.backAdmin")}
        </Anchor>
      </Stack>
    </NavBarContainer>
  );
}
